import React from 'react';
import { Users } from 'lucide-react';
import T from 'Components/Helpers/translate';
import { formatFieldName } from './FieldGrid';

/** Mirrors the friendly audience shape the agent's crmMutation tool uses for
 *  segments (base/src/core/helper/segmentAudienceBuilder.ts) — the proposal
 *  payload carries it untranslated; the executor turns it into filter blocks. */
export type SegmentCriterion = { field: string; operator: string; value: unknown };
export type SegmentAudience = {
	all?: SegmentCriterion[];
	any?: SegmentCriterion[];
	exclude?: SegmentCriterion[];
};

/** Labels for the curated field catalog (base/src/core/helper/segmentFilterCatalog.ts).
 *  Fields added to the catalog later fall back to a humanized field name. */
const FIELD_LABEL_KEYS: Record<string, string> = {
	contactName: 'default.name',
	email: 'default.email',
	title: 'default.title',
	companyName: 'default.accountName',
	isActive: 'default.active',
	score: 'default.score'
};

const GROUPS: Array<{ key: keyof SegmentAudience; labelKey: string }> = [
	{ key: 'all', labelKey: 'assistant.segmentDraft.matchAll' },
	{ key: 'any', labelKey: 'assistant.segmentDraft.matchAny' },
	{ key: 'exclude', labelKey: 'segment.exclude' }
];

const isCriterion = (value: unknown): value is SegmentCriterion =>
	typeof value === 'object' &&
	value !== null &&
	typeof (value as SegmentCriterion).field === 'string' &&
	typeof (value as SegmentCriterion).operator === 'string';

/** True when the value looks like the agent's friendly audience object —
 *  the signal WriteCard uses to swap the generic field grid for this preview. */
export function isSegmentAudience(value: unknown): value is SegmentAudience {
	if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
	const groups = GROUPS.map(g => (value as Record<string, unknown>)[g.key]);
	if (!groups.some(g => Array.isArray(g) && g.length > 0)) return false;
	return groups.every(g => g === undefined || g === null || (Array.isArray(g) && g.every(isCriterion)));
}

function fieldLabel(field: string): string {
	const key = FIELD_LABEL_KEYS[field];
	return key ? T(key) : formatFieldName(field);
}

/** Render one criterion in human terms: "Marketing score 50 – 100",
 *  "Email contains @acme.se", "Active: Yes". */
function describeCriterion(c: SegmentCriterion): { operator: string | null; value: string } {
	const v = c.value;
	if (typeof v === 'boolean') {
		return { operator: null, value: v ? T('default.yes') : T('default.no') };
	}
	if (typeof v === 'object' && v !== null && !Array.isArray(v)) {
		const { min, max } = v as { min?: unknown; max?: unknown };
		const hasMin = typeof min === 'number';
		const hasMax = typeof max === 'number';
		const range = hasMin && hasMax ? `${min} – ${max}` : hasMin ? `≥ ${min}` : hasMax ? `≤ ${max}` : '—';
		return { operator: null, value: range };
	}
	const value = Array.isArray(v) ? v.map(String).join(', ') : String(v ?? '—');
	const operator =
		c.operator === 'contains'
			? T('assistant.segmentDraft.contains')
			: c.operator === 'equals'
			? T('comparison.Equals').toLowerCase()
			: c.operator;
	return { operator, value };
}

type Props = {
	audience: SegmentAudience;
	/** Estimated audience size computed at draft time; omitted when the
	 *  backend couldn't count (estimate is decoration, not a gate). */
	matchingContacts?: number;
};

/** Read-only preview of a proposed segment's audience: the ALL / ANY / exclude
 *  criteria groups in human terms plus the estimated audience size. Rendered
 *  by WriteCard instead of the generic field grid for the `audience` field. */
const SegmentAudiencePreview = ({ audience, matchingContacts }: Props) => (
	<div className="pr-segment-preview">
		{typeof matchingContacts === 'number' && (
			<div className="pr-segment-audience-size">
				<Users style={{ width: 13, height: 13 }} />
				<span>{T('assistant.segmentDraft.estimatedAudience')}</span>
				<strong>{`≈ ${matchingContacts.toLocaleString()} ${T('default.contacts').toLowerCase()}`}</strong>
			</div>
		)}
		{GROUPS.map(({ key, labelKey }) => {
			const criteria = audience[key];
			if (!criteria?.length) return null;
			return (
				<div key={key} className="pr-segment-group">
					<div className="pr-objarray-title">{T(labelKey)}</div>
					{criteria.map((c, i) => {
						const { operator, value } = describeCriterion(c);
						return (
							<div key={i} className="pr-segment-criterion">
								<span className="pr-segment-criterion-field">{fieldLabel(c.field)}</span>
								{operator && <span className="pr-segment-criterion-op">{operator}</span>}
								<span className="pr-segment-criterion-value">{value}</span>
							</div>
						);
					})}
				</div>
			);
		})}
	</div>
);

export default SegmentAudiencePreview;
